import { z } from "zod";
import { HULL_IDS } from "./definitions.js";
import { NPC_ARCHETYPES } from "./npcs.js";
import { LOOT_CARGO_KINDS } from "./risk.js";

export const ACHIEVEMENT_VERSION = "achievements-v1";
export const ACHIEVEMENT_NPC_ARCHETYPES = NPC_ARCHETYPES.filter(id => id !== "transport");
export const achievementKeySchema = z.discriminatedUnion("category", [
  z.strictObject({ category: z.literal("npc"), kind: z.enum(ACHIEVEMENT_NPC_ARCHETYPES) }),
  z.strictObject({ category: z.literal("pvp"), kind: z.enum(HULL_IDS) }),
  z.strictObject({ category: z.literal("resource"), kind: z.enum(LOOT_CARGO_KINDS) }),
]);
export type AchievementKey = z.infer<typeof achievementKeySchema>;
export const ACHIEVEMENT_TARGETS = {
  npc: [10, 50, 200, 1000], pvp: [1, 5, 20, 100], resource: [25, 100, 500, 2500],
} as const;
export const ACHIEVEMENT_REWARDS = [
  { credits: "500", components: 15 }, { credits: "1500", components: 45 },
  { credits: "5000", components: 150 }, { credits: "15000", components: 450 },
] as const;
export const ACHIEVEMENT_KEYS: readonly AchievementKey[] = [
  ...ACHIEVEMENT_NPC_ARCHETYPES.map(kind => ({ category: "npc" as const, kind })),
  ...HULL_IDS.map(kind => ({ category: "pvp" as const, kind })),
  ...LOOT_CARGO_KINDS.map(kind => ({ category: "resource" as const, kind })),
];
export const achievementAwardSchema = z.strictObject({
  step: z.number().int().min(1).max(4), target: z.number().int().positive(),
  credits: z.string().regex(/^(0|[1-9]\d*)$/), components: z.number().int().nonnegative(),
  awardedAt: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
});
export const achievementViewSchema = z.strictObject({
  version: z.literal(ACHIEVEMENT_VERSION),
  entries: z.array(z.strictObject({ key: achievementKeySchema,
    progress: z.string().regex(/^(0|[1-9]\d*)$/), awards: z.array(achievementAwardSchema).max(4),
  })).max(128),
});
export type AchievementView = z.infer<typeof achievementViewSchema>;
export interface AchievementEvidence {
  key: AchievementKey;
  sourceId: string;
}
export const npcAchievementProofSchema = z.strictObject({
  playerId: z.uuid(), runId: z.string().min(1).max(128),
  archetype: z.enum(ACHIEVEMENT_NPC_ARCHETYPES), sourceId: z.string().min(1).max(500),
});
export type NpcAchievementProof = z.infer<typeof npcAchievementProofSchema>;
